import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '../common/Card';
import type { AgentInfo } from '../../utils/agents';
import { getAgentColor } from '../../utils/agents';

interface DepartmentCardProps {
  id: string;
  name: string;
  agents: AgentInfo[];
  cSuiteParent?: AgentInfo;
}

function getInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function DepartmentCard({ id, name, agents, cSuiteParent }: DepartmentCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="!p-0 overflow-hidden">
      {/* Header — always visible */}
      <button
        className="w-full flex items-center gap-3 p-3 hover:bg-bg-tertiary transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Department number badge */}
        <div className="w-8 h-8 rounded-md bg-bg-tertiary border border-border-subtle flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-mono font-semibold text-text-secondary">{id}</span>
        </div>

        {/* Name + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{name}</p>
          {cSuiteParent ? (
            <p className="text-[10px] text-text-muted truncate">
              Reports to: {cSuiteParent.name} ({cSuiteParent.shortName})
            </p>
          ) : null}
        </div>

        {/* Agent count + avatars */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Mini avatar row */}
          <div className="flex -space-x-1.5">
            {agents.slice(0, 6).map((agent) => (
              <div
                key={agent.slug}
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-semibold text-white border border-bg-secondary ${getAgentColor(agent.department)}`}
                title={agent.name}
              >
                {getInitials(agent.name)}
              </div>
            ))}
            {agents.length > 6 ? (
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-semibold text-text-muted bg-bg-tertiary border border-bg-secondary">
                +{agents.length - 6}
              </div>
            ) : null}
          </div>

          {/* Count badge */}
          <span className="text-[10px] text-text-muted whitespace-nowrap">
            {agents.length} agent{agents.length !== 1 ? 's' : ''}
          </span>

          {/* Chevron */}
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-muted" />
          )}
        </div>
      </button>

      {/* Expanded agent list */}
      {expanded ? (
        <div className="border-t border-border-subtle">
          {agents.map((agent) => (
            <div
              key={agent.slug}
              className="flex items-center gap-3 px-3 py-2 hover:bg-bg-tertiary transition-colors"
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0 ${getAgentColor(agent.department)}`}
              >
                {getInitials(agent.name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-text-primary truncate">{agent.name}</p>
                <p className="text-[10px] text-text-muted truncate">{agent.title}</p>
              </div>
              {agent.skills.length > 0 ? (
                <span className="text-[9px] text-text-muted bg-bg-tertiary px-1.5 py-0.5 rounded flex-shrink-0">
                  {agent.skills.length} skill{agent.skills.length !== 1 ? 's' : ''}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

export default DepartmentCard;
